import {
  Component,
  OnInit,
  AfterViewInit,
  ElementRef,
  ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/_services/auth.service';
import { CommonService } from 'src/app/_services/common.service';
import { appConfig } from 'src/app/_core/config/app-config.const';
import { DashboardService } from '../../services/dashboard.service';
import { ProfessorAnalyticsService } from './services/professor-analytics.service';
import {
  AnalyticsTab,
  AnalyticsPeriod,
  ProfessorAnalyticsBundle,
  TangramPerfRow,
  TrendDelta
} from './models/analytics.types';
import {
  ANALYTICS_PERIOD_OPTIONS,
  ANALYTICS_PERIOD_TRENDS
} from './models/period-trends.const';

const VALID_TABS: AnalyticsTab[] = [
  'overview',
  'tangram-performance',
  'content-performance',
  'learner-engagement',
  'event-tracking',
  'ai-insights',
  'reports-export'
];

// Thin orchestrator: owns route/auth/period state, delegates rendering
// to per-tab components, persists active tab in the URL query string.
@Component({
  selector: 'tangram-professor-analytics-dashboard',
  templateUrl: './professor-analytics-dashboard.component.html',
  styleUrls: ['./professor-analytics-dashboard.component.scss'],
  // Disabled so per-tab and shared component DOM picks up the
  // .professor-analytics-page namespaced styles in this file.
  encapsulation: ViewEncapsulation.None
})
export class ProfessorAnalyticsDashboardComponent implements OnInit, AfterViewInit {
  pathwayId = '';
  imgPath: string = appConfig.imagePath;
  currentUser: any;
  loading = true;
  error = '';
  unauthorized = false;
  pathwayDetail: any = null;

  activeTab: AnalyticsTab = 'overview';
  selectedPeriod: AnalyticsPeriod = 'This Month';
  readonly periodOptions = ANALYTICS_PERIOD_OPTIONS;

  bundle: ProfessorAnalyticsBundle;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private commonService: CommonService,
    private dashboardService: DashboardService,
    private analyticsService: ProfessorAnalyticsService,
    private el: ElementRef
  ) {
    this.currentUser = this.authService.currentUserValue;
    this.bundle = this.analyticsService.buildMockBundle();
  }

  ngOnInit(): void {
    this.commonService.changeView('professor-analytics');
    this.pathwayId = this.resolvePathwayIdFromRoute();

    // Restore active tab from URL on first paint AND react to back/forward
    // navigation. Persists across full page refresh.
    //
    // When the user lands here via a plain link (e.g. the header menu's
    // `routerLink="/professor-analytics"`) the URL has no `tab` param —
    // stamp the default onto the URL with replaceUrl so we don't add
    // a history entry. This keeps the URL truthful at all times.
    this.route.queryParams.subscribe((params) => {
      const tab = params['tab'] as AnalyticsTab;
      if (tab && VALID_TABS.indexOf(tab) > -1) {
        if (tab !== this.activeTab) {
          this.activeTab = tab;
          setTimeout(() => this.attachDragScroll(), 0);
        }
      } else {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { tab: this.activeTab },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });

    this.loadDashboard();
  }

  ngAfterViewInit(): void {
    this.attachDragScroll();
  }

  // ── Period & trends ─────────────────────────────────────────────
  // Tabs read trend deltas off a single map rather than each tab
  // owning its own period→key mapping.
  get trends(): { [key: string]: TrendDelta } {
    return ANALYTICS_PERIOD_TRENDS[this.selectedPeriod] || {};
  }

  // ── Tab navigation with URL persistence ─────────────────────────
  setTab(tab: AnalyticsTab): void {
    if (tab === this.activeTab) { return; }
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge'
    });
    setTimeout(() => this.attachDragScroll(), 0);
  }

  // ── Top / Lowest tangrams (used by Overview & Tangram Metrics) ──
  get topTangrams(): TangramPerfRow[] {
    return [...(this.bundle.tangramPerformanceRows || [])]
      .sort((a, b) => b.completionRate - a.completionRate)
      .slice(0, 5);
  }

  get lowTangrams(): TangramPerfRow[] {
    return [...(this.bundle.tangramPerformanceRows || [])]
      .sort((a, b) => a.completionRate - b.completionRate)
      .slice(0, 5);
  }

  // ── Top-right Export (CSV of content metrics) ───────────────────
  exportDashboard(): void {
    const rows = (this.bundle.contentMetrics || []).map((r) =>
      `${r.title},${r.type},${r.views},${r.completionRate}%,${r.avgTimeSpent},${r.rating}`
    );
    const csv = ['Content,Type,Views,Completion,Avg Time,Rating', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${this.selectedPeriod.replace(/\s/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  goBack(): void {
    if (this.pathwayId) {
      this.router.navigate(['/dashboard', this.pathwayId]);
      return;
    }
    this.router.navigate(['/my-tangram']);
  }

  // ── Loading ─────────────────────────────────────────────────────
  private loadDashboard(): void {
    this.loading = true;
    this.error = '';
    this.unauthorized = false;

    if (!this.pathwayId || this.pathwayId === 'local-pathway') {
      this.loadFromMock();
      return;
    }

    this.dashboardService.getPathwayDetail(this.pathwayId).subscribe(
      (response: any) => {
        const pathway = (response && response.data) || null;
        if (!pathway) {
          this.loadFromMock();
          return;
        }
        this.pathwayDetail = pathway;
        if (!this.analyticsService.canViewProfessorAnalytics(pathway, this.currentUser)) {
          this.unauthorized = true;
          this.loading = false;
          return;
        }
        this.analyticsService.buildBundleFromPathway(pathway, this.pathwayId).subscribe(
          (bundle) => {
            this.bundle = bundle;
            this.loading = false;
          },
          () => this.loadFromMock()
        );
      },
      () => this.loadFromMock()
    );
  }

  private loadFromMock(): void {
    this.bundle = this.analyticsService.buildMockBundle();
    this.pathwayDetail = {
      _id: this.pathwayId || 'demo',
      title: 'My Published Tangrams',
      description: 'Aggregated analytics across all Tangrams you have created and made public for students.',
      createdBy: this.currentUser && this.currentUser._id ? this.currentUser._id : 'demo'
    };
    this.loading = false;
  }

  // ── Helpers ─────────────────────────────────────────────────────
  private resolvePathwayIdFromRoute(): string {
    let current: ActivatedRoute | null = this.route;
    while (current) {
      const paramId = current.snapshot && current.snapshot.params ? current.snapshot.params.pathwayId : '';
      if (paramId) { return paramId; }
      current = current.parent;
    }
    const fromStorage = localStorage.getItem('pathwayId');
    if (fromStorage) { return fromStorage; }
    const match = (this.router.url || '').match(/dashboard\/([^/]+)/);
    return match && match[1] ? match[1] : 'local-pathway';
  }

  private attachDragScroll(): void {
    const host = this.el.nativeElement as HTMLElement;
    const selectors = ['.dash-tabs-scroll', '.table-responsive'];

    selectors.forEach((selector) => {
      host.querySelectorAll<HTMLElement>(selector).forEach((el) => {
        if ((el as any).__dragScrollAttached) { return; }
        (el as any).__dragScrollAttached = true;

        let isDown = false;

        el.addEventListener('mousedown', (e: MouseEvent) => {
          isDown = true;
          el.classList.add('is-dragging');
          e.preventDefault();
        });

        const stop = () => { isDown = false; el.classList.remove('is-dragging'); };
        document.addEventListener('mouseup', stop);
        el.addEventListener('mouseleave', stop);

        el.addEventListener('mousemove', (e: MouseEvent) => {
          if (!isDown) { return; }
          e.preventDefault();
          el.scrollLeft -= e.movementX;
        });
      });
    });
  }
}
